/**
 * B632 (e2) — the ADD sheet's controller. Every write is the buyer's own,
 * under the signed-in session: cart.create + cart.add (their cart, the
 * booking's night), `order.lines.add` (a NEW linked order, recorded default
 * (ii)), `payment.intent.create` (their own checkout) and the provider's
 * card element. After the provider confirms, the page polls the new order
 * until the webhook confirms it (§7 step 5, the storefront's cadence), then
 * opens its page — the new tickets live there. Closing after the hold lets
 * the seats go (order.abandon); a refusal is the server's sentence.
 */
import { useEffect, useRef, useState } from "react";
import { createPaymentGateway, loadStripeFactory, type PaymentGateway, type StripeFactory } from "../lib/gateway.js";
import { meOrderUrl, type MeOrderDetail } from "../lib/me.js";
import { meAddChoices, type MeAddChoice, type MeAddVariant } from "../lib/meAdd.js";
import { meMoveTarget, meMoveWhere } from "../lib/meMove.js";
import { formatMoney } from "../lib/money.js";
import { ORDER_POLL_INTERVAL_MS } from "../lib/orderPoll.js";
import { documentPaymentAppearance } from "../lib/paymentAppearance.js";
import type { MeAddStep } from "./meAddSheet.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

/** How many times the page asks before it hands the buyer to My orders (the webhook's own clock). */
const POLLS = 20;

const FAILED = "We couldn't add your tickets just now. Nothing was charged — try again in a moment.";
/* B839 — a card confirm that THREW (Stripe.js unreachable), not one the provider declined: the intent can succeed only once, so PAY again is safe */
const CONFIRM_THREW = "The payment didn't go through — nothing was confirmed. Press Pay again; this payment can only be charged once.";
const READ_REFUSED = "We couldn't read the tickets just now. Nothing changed.";

const sameCounts = (a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>): boolean => {
  const live = (r: Readonly<Record<string, number>>) => Object.entries(r).filter(([, n]) => n > 0).sort(([x], [y]) => x.localeCompare(y));
  return JSON.stringify(live(a)) === JSON.stringify(live(b));
};

function refusalOf(e: unknown, fallback: string): string {
  return typeof e === "object" && e !== null && (e as { code?: unknown }).code === "REFUSED" && e instanceof Error ? e.message : fallback;
}

export interface MeAddState {
  readonly nightWords: string;
  readonly choices: readonly MeAddChoice[] | undefined;
  readonly counts: Readonly<Record<string, number>>;
  readonly step: MeAddStep;
  readonly busy: boolean;
  readonly error: string | undefined;
  /** B839 — the ticket types could not be read: said with its way back (R18), never "nothing to add" — that is not known. */
  readonly readError?: string | undefined;
}

export function useMeAdd(opts: {
  /** The signed-in caller, or undefined while signed out. */
  readonly signedIn: Caller | undefined;
  readonly tenantId: string | undefined;
  readonly channelId: string | undefined;
  readonly order: MeOrderDetail | undefined;
  readonly go: (url: string) => void;
  readonly stripe?: (() => Promise<StripeFactory | undefined>) | undefined;
  readonly pollMs?: number | undefined;
  readonly newCartId?: (() => string) | undefined;
}) {
  const [state, setState] = useState<MeAddState | undefined>(undefined);
  const held = useRef<string | undefined>(undefined);
  /* B839 — what the live hold holds: a retried CONTINUE with the same counts reuses it (no second held order); changed counts let it go first */
  const heldFor = useRef<{ readonly counts: Readonly<Record<string, number>>; readonly total: { amount: number; currency: string } } | undefined>(undefined);
  const gateway = useRef<PaymentGateway | undefined>(undefined);
  const target = opts.order === undefined ? undefined : meMoveTarget(opts.order);
  const offered = opts.order?.selfService?.addTickets === true && target !== undefined;

  const mountedCard = state?.step.kind === "pay" && state.step.card === "mounted";
  useEffect(() => {
    if (mountedCard) gateway.current?.mount("#me-add-card");
  }, [mountedCard]);

  const patch = (p: Partial<MeAddState>): void => setState((s) => (s === undefined ? s : { ...s, ...p }));

  const open = (): void => {
    const { signedIn, channelId } = opts;
    if (!offered || signedIn === undefined || channelId === undefined) return;
    held.current = undefined;
    heldFor.current = undefined;
    gateway.current = undefined;
    setState({ nightWords: meMoveWhere(target.fromSlotId), choices: undefined, counts: {}, step: { kind: "pick" }, busy: false, error: undefined });
    readChoices();
  };

  const readChoices = (): void => {
    const { signedIn, channelId } = opts;
    if (signedIn === undefined || channelId === undefined || target === undefined) return;
    patch({ choices: undefined, readError: undefined });
    void signedIn
      .call("catalog.get", { channelId, productId: target.productId })
      .then((r) => patch({ choices: meAddChoices((r as { product?: { variants: readonly MeAddVariant[] } }).product?.variants ?? []) }))
      .catch(() => patch({ readError: READ_REFUSED }));
  };

  /* B839 — the counts hold still while CONTINUE flies (R16): the summary and the door say what is being held */
  const count = (variantId: string, n: number): void =>
    setState((s) => (s === undefined || s.busy ? s : { ...s, counts: { ...s.counts, [variantId]: n }, error: undefined }));

  /** CONTINUE: the buyer's cart, the hold (order.lines.add), the intent, the card. */
  const proceed = (): void => {
    const { signedIn, channelId, order } = opts;
    if (state === undefined || state.busy || signedIn === undefined || channelId === undefined || order === undefined || target === undefined) return;
    const counts = state.counts;
    patch({ busy: true, error: undefined });
    void (async () => {
      /* B839 — a hold from a refused press: the same counts reuse it; changed counts let it go before a fresh one is minted */
      const stale = held.current;
      if (stale !== undefined && heldFor.current !== undefined && !sameCounts(heldFor.current.counts, counts)) {
        held.current = undefined;
        heldFor.current = undefined;
        await signedIn.call("order.abandon", { orderId: stale }).catch(() => undefined);
      }
      if (held.current === undefined) {
        const cartId = opts.newCartId?.() ?? `me-add-${crypto.randomUUID()}`;
        await signedIn.call("cart.create", { cartId, channelId, currency: order.total.currency });
        for (const [variantId, quantity] of Object.entries(counts)) {
          if (quantity > 0) await signedIn.call("cart.add", { cartId, productId: target.productId, variantId, slotId: target.fromSlotId, quantity });
        }
        const minted = (await signedIn.call("order.lines.add", { orderId: order.orderId, cartId })) as { orderId: string; totals: { total: { amount: number; currency: string } } };
        held.current = minted.orderId;
        heldFor.current = { counts, total: minted.totals.total };
      }
      const added = { orderId: held.current, totals: { total: heldFor.current!.total } };
      const intent = (await signedIn.call("payment.intent.create", { orderId: added.orderId, amount: added.totals.total, provider: "stripe" })) as {
        clientSecret: string;
        publishableKey?: string;
        stripeAccount?: string;
      };
      const built = createPaymentGateway({
        factory: await (opts.stripe ?? loadStripeFactory)(),
        publishableKey: intent.publishableKey,
        ...(intent.stripeAccount !== undefined ? { stripeAccount: intent.stripeAccount } : {}),
        clientSecret: intent.clientSecret,
        appearance: documentPaymentAppearance(),
      });
      gateway.current = built.gateway;
      patch({
        busy: false,
        step: { kind: "pay", total: formatMoney(added.totals.total), card: built.gateway !== undefined ? "mounted" : { unavailable: built.unavailable } },
      });
    })().catch((e: unknown) => patch({ busy: false, error: refusalOf(e, FAILED) }));
  };

  /** PAY: the provider confirms; the page waits for the webhook, then opens the new order. */
  const pay = (): void => {
    const { signedIn, tenantId } = opts;
    const orderId = held.current;
    if (state === undefined || state.busy || gateway.current === undefined || orderId === undefined || signedIn === undefined || tenantId === undefined) return;
    patch({ busy: true, error: undefined });
    void gateway.current.confirm().catch(() => ({ ok: false as const, message: CONFIRM_THREW })).then(async (out) => {
      if (!out.ok) {
        patch({ busy: false, error: out.message });
        return;
      }
      patch({ busy: false, step: { kind: "confirming" } });
      for (let n = 0; n < POLLS; n += 1) {
        await new Promise((r) => setTimeout(r, opts.pollMs ?? ORDER_POLL_INTERVAL_MS));
        const got = (await signedIn.call("order.get", { orderId }).catch(() => undefined)) as { order?: { state: string } } | undefined;
        if (got?.order?.state === "CONFIRMED") break;
      }
      /* paid either way: the new order's page says where it stands */
      held.current = undefined;
      heldFor.current = undefined;
      opts.go(meOrderUrl(tenantId, orderId));
    });
  };

  /** CANCEL / ✕: a held order lets its seats go now, not at the hold's end. */
  const close = (): void => {
    if (state?.step.kind === "confirming") return;
    const orderId = held.current;
    held.current = undefined;
    heldFor.current = undefined;
    gateway.current?.unmount();
    gateway.current = undefined;
    setState(undefined);
    if (orderId !== undefined) void opts.signedIn?.call("order.abandon", { orderId }).catch(() => undefined);
  };

  return { offered, state, open, reread: readChoices, count, proceed, pay, close };
}
